import axios from "axios";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  getCustomerName,
  getCustomerEmail,
  getCustomerPhone,
} from "@/lib/safe-access";
import { CustomerType } from "@/api.types.v2";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, CheckCircle2, XCircle } from "lucide-react";
import { useState, useEffect } from "react";
import {
  useCustomers,
  type CustomerListRow,
  useSyncCustomerWithStripe,
  useDeleteBusinessAccount,
  useUpdateCustomerTestAccount,
} from "@/api/customers";
import { useSendPasswordResetEmail } from "@/api/use-user-api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { DepositCreditDialog } from "./deposit-credit-dialog";
import { CustomerVouchersDialog } from "./customer-vouchers-dialog";
import { CustomerBalanceCell } from "./customer-balance-cell";
import { PaymentAccessDialog } from "./payment-access-dialog";
import { Wallet, CreditCard } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const statusColors: Record<string, string> = {
  ACTIVE: "bg-green-500",
  INACTIVE: "bg-gray-500",
  DELETED: "bg-red-500",
};

const typeColors: Record<string, string> = {
  INDIVIDUAL: "bg-blue-500",
  BUSINESS: "bg-purple-500",
};

export function CustomersTable() {
  const [depositCreditDialogOpen, setDepositCreditDialogOpen] = useState(false);
  const [paymentAccessDialogOpen, setPaymentAccessDialogOpen] = useState(false);
  const [vouchersDialogOpen, setVouchersDialogOpen] = useState(false);
  const [vouchersCustomer, setVouchersCustomer] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<{
    id: string;
    firstName: string;
    lastName: string;
    allowedPaymentTermsIds?: string[];
    netTermsCreditLimit?: number | null;
    isTestAccount?: boolean;
  } | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  useEffect(() => {
    if (searchTerm === debouncedSearchTerm) return;
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, debouncedSearchTerm]);

  const {
    data: customersResponse,
    isLoading,
    isFetching,
    error,
    isCoolingDown,
    refetch,
  } = useCustomers({
    page,
    limit,
    search: debouncedSearchTerm,
  });

  const displayedResponse = customersResponse;
  const rateLimited =
    axios.isAxiosError(error) && error.response?.status === 429;

  const selectCustomer = (customer: CustomerListRow) =>
    setSelectedCustomer({
      id: customer.id,
      firstName: customer.firstName ?? "",
      lastName: customer.lastName ?? "",
      allowedPaymentTermsIds: customer.allowedPaymentTermsIds,
      netTermsCreditLimit: customer.netTermsCreditLimit,
      isTestAccount: customer.isTestAccount,
    });

  const syncWithStripeMutation = useSyncCustomerWithStripe();
  const deleteBusinessAccountMutation = useDeleteBusinessAccount();
  const toggleTestAccount = useUpdateCustomerTestAccount();

  const sendPasswordResetEmail = useSendPasswordResetEmail({
    isCustomer: true,
    onSuccess: () => {
      toast.success("Password reset email sent successfully");
    },
  });

  const handleSyncWithStripe = async (customerId: string) => {
    if (!customerId) return;
    try {
      await syncWithStripeMutation.mutateAsync({ customerId });
      toast.success("Sync with Stripe initiated.");
    } catch (error) {
      console.error("Error syncing with Stripe:", error);
      toast.error("Failed to sync with Stripe.");
    }
  };

  const handleAssignBusinessAccount = (customerId: string) => {
    console.log("Assign business account to:", customerId);
  };

  const handleDeleteBusinessAccount = async (
    customerId: string,
    businessAccountId: string
  ) => {
    if (!customerId || !businessAccountId) return;
    if (
      !confirm(
        "Are you sure you want to delete this business account? The customer will be reverted to an individual account."
      )
    ) {
      return;
    }
    try {
      await deleteBusinessAccountMutation.mutateAsync({
        customerId,
        businessAccountId,
      });
      toast.success("Business account deleted successfully");
    } catch (error) {
      console.error("Error deleting business account:", error);
      toast.error("Failed to delete business account");
    }
  };

  if (isLoading && !displayedResponse) {
    return <div>Loading...</div>;
  }

  const customers = displayedResponse?.data ?? [];
  const pagination = displayedResponse?.pagination;

  return (
    <div className="space-y-4">
      <div className="flex justify-end mb-4">
        <Input
          placeholder="Search customers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
      </div>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {rateLimited
              ? "Too many requests. Please wait before retrying."
              : "Could not load customers. Please retry."}
            {displayedResponse &&
              ` Showing the last loaded page (${pagination?.page}).`}
            <Button
              variant="outline"
              className="ml-2"
              disabled={isFetching || isCoolingDown}
              onClick={() => void refetch()}
            >
              {isCoolingDown ? "Retry after cooldown" : "Retry"}
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {isFetching && displayedResponse && (
        <p role="status">Loading customers...</p>
      )}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Business Details</TableHead>
              <TableHead>Enabled Carriers</TableHead>
              <TableHead>Credit Balance</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayedResponse && customers.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center">
                  No customers found matching your search.
                </TableCell>
              </TableRow>
            )}
            {customers.map((customer) => (
              <TableRow key={customer.id}>
                <TableCell className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback>
                      {customer.firstName?.[0]}
                      {customer.lastName?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{getCustomerName(customer)}</p>
                      {customer.isTestAccount && (
                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                          Test
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      #{customer.customerNumber}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    className={`${typeColors[customer.type]} text-white`}
                    variant="secondary"
                  >
                    {customer.type}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    className={`${statusColors[customer.status]} text-white`}
                    variant="secondary"
                  >
                    {customer.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <TooltipProvider>
                    <div className="flex items-center gap-1">
                      <p>{getCustomerPhone(customer)}</p>
                      {customer.phone && (
                        <Tooltip>
                          <TooltipTrigger>
                            {customer.phoneVerified ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                            ) : (
                              <XCircle className="h-3.5 w-3.5 text-gray-300" />
                            )}
                          </TooltipTrigger>
                          <TooltipContent>
                            {customer.phoneVerified
                              ? `Verified ${customer.phoneVerifiedAt ? new Date(customer.phoneVerifiedAt).toLocaleDateString() : ""}`
                              : "Not verified"}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <p className="text-sm text-gray-500">
                        {getCustomerEmail(customer)}
                      </p>
                      {customer.email && (
                        <Tooltip>
                          <TooltipTrigger>
                            {customer.emailVerified ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                            ) : (
                              <XCircle className="h-3.5 w-3.5 text-gray-300" />
                            )}
                          </TooltipTrigger>
                          <TooltipContent>
                            {customer.emailVerified
                              ? `Verified ${customer.emailVerifiedAt ? new Date(customer.emailVerifiedAt).toLocaleDateString() : ""}`
                              : "Not verified"}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TooltipProvider>
                </TableCell>
                <TableCell>
                  {customer.businessAccount ? (
                    <div>
                      <p className="font-medium">
                        {customer.businessAccount.companyName}
                      </p>
                      <p className="text-sm text-gray-500">
                        Tax #: {customer.businessAccount.taxNumber ?? "N/A"}
                      </p>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">
                      No business account
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {customer.carrierConnections &&
                  customer.carrierConnections.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {customer.carrierConnections.map((conn) => (
                        <Badge key={conn.carrier.name} variant="outline">
                          {conn.carrier.name}
                          {conn.tier ? ` · ${conn.tier.name}` : ""}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">No carriers</span>
                  )}
                </TableCell>
                <TableCell>
                  <CustomerBalanceCell
                    customerId={customer.id}
                    customerName={getCustomerName(customer)}
                    balance={customer.creditBalance}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          if (customer.id) {
                            sendPasswordResetEmail.mutate(customer.id);
                          }
                        }}
                        disabled={
                          sendPasswordResetEmail.isPending || !customer.id
                        }
                      >
                        {sendPasswordResetEmail.isPending
                          ? "Sending..."
                          : "Send Password Reset Email"}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={(e) => {
                          e.preventDefault();
                          if (!customer.id) return;
                          setVouchersCustomer({
                            id: customer.id,
                            name: getCustomerName(customer),
                          });
                          setVouchersDialogOpen(true);
                        }}
                      >
                        Vouchers
                      </DropdownMenuItem>

                      {customer.billing && (
                        <DropdownMenuItem
                          onClick={() => handleSyncWithStripe(customer.id)}
                          disabled={syncWithStripeMutation.isPending}
                        >
                          {syncWithStripeMutation.isPending &&
                          syncWithStripeMutation.variables?.customerId ===
                            customer.id
                            ? "Syncing..."
                            : "Sync with Stripe"}
                        </DropdownMenuItem>
                      )}

                      <DropdownMenuItem
                        onSelect={() => {
                          selectCustomer(customer);
                          setDepositCreditDialogOpen(true);
                        }}
                      >
                        <Wallet className="mr-2 h-4 w-4" />
                        Top up
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => {
                          selectCustomer(customer);
                          setPaymentAccessDialogOpen(true);
                        }}
                      >
                        <CreditCard className="mr-2 h-4 w-4" />
                        Manage Payment Access
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={() =>
                          toggleTestAccount.mutate({
                            customerId: customer.id,
                            isTestAccount: !customer.isTestAccount,
                          })
                        }
                      >
                        {customer.isTestAccount
                          ? "Remove Test Account"
                          : "Set as Test Account"}
                      </DropdownMenuItem>

                      {customer.type === CustomerType.INDIVIDUAL && (
                        <DropdownMenuItem
                          onClick={() =>
                            handleAssignBusinessAccount(customer.id)
                          }
                        >
                          Add Business Account
                        </DropdownMenuItem>
                      )}

                      {customer.businessAccount && (
                        <DropdownMenuItem
                          onClick={() =>
                            handleDeleteBusinessAccount(
                              customer.id,
                              customer.businessAccount!.id
                            )
                          }
                          disabled={deleteBusinessAccountMutation.isPending}
                          className="text-red-600 focus:text-red-600"
                        >
                          {deleteBusinessAccountMutation.isPending
                            ? "Deleting..."
                            : "Delete Business Account"}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {pagination && (
        <div className="flex items-center justify-between px-2">
          <div className="text-sm text-gray-500">
            Showing page {pagination?.page ?? 1} of{" "}
            {pagination?.totalPages ?? 1}. Total {pagination?.totalCount ?? 0}{" "}
            customers.
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((pagination?.page ?? page) - 1)}
              disabled={
                !pagination?.hasPreviousPage || isFetching || isCoolingDown
              }
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((pagination?.page ?? page) + 1)}
              disabled={!pagination?.hasNextPage || isFetching || !!error}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {selectedCustomer && (
        <>
          {depositCreditDialogOpen && (
            <DepositCreditDialog
              open={depositCreditDialogOpen}
              onOpenChange={setDepositCreditDialogOpen}
              customer={{
                id: selectedCustomer.id,
                firstName: selectedCustomer.firstName || "",
                lastName: selectedCustomer.lastName || "",
                isTestAccount: selectedCustomer.isTestAccount,
              }}
              onSuccess={() => {
                setDepositCreditDialogOpen(false);
                setSelectedCustomer(null);
              }}
            />
          )}

          {paymentAccessDialogOpen && (
            <PaymentAccessDialog
              open={paymentAccessDialogOpen}
              onOpenChange={setPaymentAccessDialogOpen}
              customer={selectedCustomer}
            />
          )}
        </>
      )}

      {vouchersCustomer && (
        <CustomerVouchersDialog
          customerId={vouchersCustomer.id}
          customerName={vouchersCustomer.name}
          open={vouchersDialogOpen}
          onOpenChange={(open) => {
            setVouchersDialogOpen(open);
            if (!open) setVouchersCustomer(null);
          }}
        />
      )}
    </div>
  );
}
